import { DateTime } from 'luxon'

const TIME_SIMPLE_WITH_ZONE: Intl.DateTimeFormatOptions = {
  hour: 'numeric',
  minute: '2-digit',
  timeZoneName: 'shortOffset',
}

/**
 * Format a time, appending the timezone (e.g. "9:45 AM GMT-4") ONLY when the
 * displayed time's UTC offset differs from the reference time's offset — i.e.
 * the site is in a different timezone than the device. Appends the weekday when
 * the date differs. Mirrors the v1 crew app, so "GMT-4" only shows when it's
 * actually meaningful (a cross-timezone job), not always.
 */
export const formatRelativeTime = (
  displayDate: DateTime,
  relativeDate: DateTime,
  options?: { omitMinutesIfZero?: boolean },
): string => {
  let format: Intl.DateTimeFormatOptions =
    displayDate.offset !== relativeDate.offset
      ? TIME_SIMPLE_WITH_ZONE
      : DateTime.TIME_SIMPLE
  if (options?.omitMinutesIfZero && displayDate.minute === 0) {
    // Intl ignores explicitly-undefined fields, so this drops the minutes
    format = { ...format, minute: undefined }
  }
  const timeString = displayDate.toLocaleString(format)
  if (displayDate.toISODate() !== relativeDate.toISODate()) {
    return `${timeString} ${displayDate.toFormat('ccc')}`
  }
  return timeString
}

/**
 * Format a time against a route/assignment reference date, so the weekday
 * suffix (e.g. "Mon") appears when the time falls on a different calendar day
 * than the route — matching v1, which compares against the assignment date,
 * not the device's "now". Falls back to the time's own local zone when no
 * reference date is available (then a suffix only shows if the zone itself
 * crosses a day boundary).
 */
export const formatRouteRelativeTime = (
  datetime: DateTime,
  referenceDate: DateTime | undefined,
): string => formatRelativeTime(datetime, (referenceDate ?? datetime).toLocal())

/**
 * Parse an ISO datetime string.
 * If a timezone is provided, the DateTime is set to that zone.
 * Otherwise the offset from the ISO string itself is used.
 */
export const parseDatetime = (
  time: string | null | undefined,
  timezone?: string | null,
): DateTime | undefined => {
  if (!time) return undefined
  const dt = timezone
    ? DateTime.fromISO(time).setZone(timezone)
    : DateTime.fromISO(time, { setZone: true })
  return dt.isValid ? dt : undefined
}

/**
 * Compute the midpoint DateTime between two DateTimes.
 */
export const middleDateTime = (start: DateTime, end: DateTime): DateTime =>
  start.plus({ milliseconds: end.diff(start).as('milliseconds') / 2 })

/**
 * Derive a route date from the route's start/end local times.
 * Returns the midpoint's day start.
 */
export const routeDate = (
  route:
    | { startTimeLocal?: string | null; endTimeLocal?: string | null }
    | null
    | undefined,
): DateTime | undefined => {
  const startRaw = route?.startTimeLocal
  const endRaw = route?.endTimeLocal
  if (!startRaw || !endRaw) return undefined

  const start = parseDatetime(startRaw)
  const end = parseDatetime(endRaw)
  if (!start || !end) return undefined

  return middleDateTime(start, end).startOf('day')
}

/** Compact departure time as v1: "7AM" / "7:30AM". */
export const formatAssignmentLeaveTime = (
  assignment:
    | {
        route?: {
          startTimeLocal?: string | null
          endTimeLocal?: string | null
        } | null
      }
    | null
    | undefined,
): string | undefined => {
  const startTime = parseDatetime(assignment?.route?.startTimeLocal)
  if (!startTime) return undefined
  const date = assignmentDate(assignment)
  if (!date) return undefined
  return formatCompactRelativeTime(startTime, date)
}

/** Compact time against a reference date, as v1: "7AM" / "7:30AM" / "7AM Tue". */
export const formatCompactRelativeTime = (
  datetime: DateTime,
  referenceDate: DateTime | undefined,
): string =>
  formatRelativeTime(datetime, (referenceDate ?? datetime).toLocal(), {
    omitMinutesIfZero: true,
  }).replace(' ', '')

/**
 * Get the assignment date from its route.
 */
export const assignmentDate = (
  assignment:
    | {
        route?: {
          startTimeLocal?: string | null
          endTimeLocal?: string | null
        } | null
      }
    | null
    | undefined,
): DateTime | undefined => {
  if (!assignment) return undefined
  return routeDate(assignment.route)
}
